import React from 'react';
import moment, { Moment } from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

/* eslint-disable restrict-imports/restrict-imports */
import { makeStyles } from 'design-system/product/theme';
import { Typography } from 'react-scale/components/design-system/product';

// Components
import DateFilter from './DateFilter';

import Tooltip from 'design-system/Tooltip';

const MAX_NUM_DAYS = 60;

interface DateFilterWrapperProps {
  startDate: Moment | undefined;
  endDate: Moment | undefined;
  setStartDate: (date: Moment | undefined) => void;
  setEndDate: (date: Moment | undefined) => void;
  maxNumDays?: number;
  format?: string;
}

const DateFilterWrapper: React.FC<DateFilterWrapperProps> = ({
  startDate,
  endDate,
  setStartDate,
  setEndDate,
  maxNumDays,
  format,
}) => {
  const classes = useStyles();

  const maxDays = maxNumDays ?? MAX_NUM_DAYS;

  const handleStartDate = (newStartDate: Moment | undefined) => {
    if (newStartDate && endDate && newStartDate > endDate) {
      return;
    }
    if (endDate && newStartDate && moment.duration(endDate.diff(newStartDate)).asDays() > maxDays) {
      setEndDate(moment(newStartDate).add(maxDays, 'days').startOf('day'));
    }
    setStartDate(newStartDate);
  };

  const handleEndDate = (newEndDate: Moment | undefined) => {
    if (newEndDate && startDate && newEndDate < startDate) {
      return;
    }
    if (startDate && newEndDate && moment.duration(newEndDate.diff(startDate)).asDays() > maxDays) {
      setStartDate(moment(newEndDate).subtract(maxDays, 'days').startOf('day'));
    }
    setEndDate(newEndDate);
  };

  return (
    <div className={classes.dateFilter}>
      <FontAwesomeIcon icon="calendar" />
      <DateFilter
        label="Select"
        date={startDate}
        onChangeDate={(e: Moment | undefined) => handleStartDate(e)}
        format={format || 'MMM D'}
      />
      <Typography color="primary">-</Typography>
      <DateFilter
        label="Select"
        date={endDate}
        onChangeDate={(e: Moment | undefined) => handleEndDate(e)}
        format={format || 'MMM D'}
        maxDate={moment(startDate).add(maxDays, 'days').startOf('day')}
      />
      <Tooltip
        title={`Maximum end date must be within ${maxDays} days of selected start date`}
        className={classes.tooltip}
      >
        <div>
          <FontAwesomeIcon icon="info-circle" />
        </div>
      </Tooltip>
    </div>
  );
};

const useStyles = makeStyles(theme => ({
  dateFilter: {
    color: theme.palette.primary.main,
    flexDirection: 'row',
    display: 'flex',
    gap: theme.spacing(0.5),
    alignItems: 'center',
  },
  tooltip: {
    cursor: 'pointer',
  },
}));

export default DateFilterWrapper;
